import React, { useEffect, useState, useCallback } from 'react';
import { listFiles, downloadFile, updateFile, errMsg } from './aiPaperApi';
import { CLASSES, SUBJECT_PRESETS } from './aiPaperUtils';
import { ui } from './ui';

export const CATEGORY_LABEL = {
  syllabus: 'Syllabus', reference: 'Reference Sheet', previous_paper: 'Previous Exam Paper', style_sample: 'Style Sample'
};
export const fmtSize = (b = 0) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, color: '#3730a3', background: '#eef2ff', whiteSpace: 'nowrap' };
const td = { padding: '7px 10px', fontSize: 13, borderTop: '1px solid #e2e8f0', verticalAlign: 'middle' };
const fileCell = { ...td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 700 };

function EditCells({ f, editing, setEditing, showTeacher, onSave }) {
  return (
    <>
      <td style={td}>
        <select style={ui.input} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </td>
      <td style={td}>{fmtSize(f.size)}</td>
      <td style={td}>{new Date(f.uploadedAt).toLocaleString('en-IN')}</td>
      {showTeacher && <td style={td}>{f.teacherName}</td>}
      <td style={td}>
        <input style={{ ...ui.input, width: 110, marginBottom: 4 }} value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} />
        <select style={ui.input} value={editing.class} onChange={(e) => setEditing({ ...editing, class: e.target.value })}>
          <option value="">-</option>{CLASSES.map((c) => <option key={c}>{c}</option>)}
        </select>
      </td>
      <td style={td}>
        <button style={ui.btnSmall} onClick={onSave}>Save</button>{' '}
        <button style={ui.btnSmall} onClick={() => setEditing(null)}>Cancel</button>
      </td>
    </>
  );
}

function ViewCells({ f, showTeacher, onDownload, onEdit }) {
  return (
    <>
      <td style={td}>{CATEGORY_LABEL[f.category] || f.category}</td>
      <td style={td}>{fmtSize(f.size)}</td>
      <td style={td}>{new Date(f.uploadedAt).toLocaleString('en-IN')}</td>
      {showTeacher && <td style={td}>{f.teacherName || '-'}</td>}
      <td style={td}>{f.subject || '-'} · {f.class ? `Class ${f.class}` : '-'}</td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        <button style={ui.btnSmall} onClick={onDownload}>⬇ Download</button>{' '}
        <button style={ui.btnSmall} onClick={onEdit}>✏️</button>
      </td>
    </>
  );
}

// Dedicated file-management table. Teachers see their own files; the principal passes
// pre-loaded `files` for a chosen teacher (or omits it to list everything).
function FileTable({ user, files: given, showTeacher = false, refreshKey = 0 }) {
  const [rows, setRows] = useState(given || null);
  const [filters, setFilters] = useState({ category: '', subject: '', class: '' });
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    if (given) return;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    listFiles(user, params).then(setRows).catch((e) => setErr(errMsg(e)));
  }, [user, filters, given]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (given) setRows(given); }, [given]);

  const saveEdit = async () => {
    try {
      const upd = await updateFile(user, editing.id, { subject: editing.subject, class: editing.class, category: editing.category });
      setRows((r) => r.map((x) => (x._id === upd._id ? { ...x, ...upd } : x)));
      setEditing(null);
    } catch (e) { setErr(errMsg(e)); }
  };
  const shown = (rows || []).filter((f) => !given
    || ((!filters.category || f.category === filters.category) && (!filters.subject || f.subject === filters.subject) && (!filters.class || f.class === filters.class)));

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <select style={{ ...ui.input, width: 'auto' }} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All types</option>
          {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input style={{ ...ui.input, width: 140 }} list="ft-subjects" placeholder="Subject" value={filters.subject} onChange={(e) => setFilters({ ...filters, subject: e.target.value })} />
        <datalist id="ft-subjects">{SUBJECT_PRESETS.map((s) => <option key={s} value={s} />)}</datalist>
        <select style={{ ...ui.input, width: 'auto' }} value={filters.class} onChange={(e) => setFilters({ ...filters, class: e.target.value })}>
          <option value="">All classes</option>
          {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      {err && <div style={ui.errorBox}>{err}</div>}
      {!rows ? <div style={{ fontSize: 13 }}>Loading…</div> : shown.length === 0 ? (
        <div style={{ fontSize: 13, color: '#64748b' }}>No files found.</div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={th}>File</th><th style={th}>Type</th><th style={th}>Size</th><th style={th}>Uploaded</th>
                {showTeacher && <th style={th}>Teacher</th>}
                <th style={th}>Subject / Class</th><th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((f) => (
                <tr key={f._id}>
                  <td style={fileCell} title={f.originalName}>{f.fileType === 'image' ? '🖼️' : '📕'} {f.originalName}</td>
                  {editing?.id === f._id
                    ? <EditCells f={f} editing={editing} setEditing={setEditing} showTeacher={showTeacher} onSave={saveEdit} />
                    : <ViewCells f={f} showTeacher={showTeacher}
                        onDownload={() => downloadFile(user, f._id, f.originalName).catch((e) => setErr(errMsg(e)))}
                        onEdit={() => setEditing({ id: f._id, subject: f.subject || '', class: f.class || '', category: f.category })} />}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FileTable;

